import { useStorage } from '@vueuse/core'

/**
 * Store for caching dropdown data in localStorage
 * This enables complete offline functionality for dropdowns in reports
 */
export const useDropdownDataStore = defineStore('dropdownDataStore', () => {
  // Cached dropdown data in localStorage
  const products = ref(useStorage('dropdown_products', [] as any[], localStorage))
  const projectTypes = ref(useStorage('dropdown_project_types', [] as any[], localStorage))
  const users = ref(useStorage('dropdown_users', [] as any[], localStorage))
  const departments = ref(useStorage('dropdown_departments', [] as any[], localStorage))
  const projectPhases = ref(useStorage('dropdown_project_phases', [] as any[], localStorage))
  const calendarEventStatuses = ref(
    useStorage('dropdown_calendar_event_statuses', [] as any[], localStorage)
  )
  const calendarCategories = ref(
    useStorage('dropdown_calendar_categories', [] as any[], localStorage)
  )
  const damageReport = ref(useStorage('dropdown_damage_report', [] as any[], localStorage))

  // Track last fetch time to know if data is stale
  const lastFetchTime = ref(useStorage('dropdown_last_fetch', 0, localStorage))

  // Track if data is being fetched
  const isFetching = ref(false)

  // Track download progress for each data type
  const downloadProgress = ref<Record<string, 'pending' | 'loading' | 'done' | 'error'>>({
    products: 'pending',
    projectTypes: 'pending',
    users: 'pending',
    departments: 'pending',
    projectPhases: 'pending',
    calendarEventStatuses: 'pending',
    calendarCategories: 'pending',
    damageReport: 'pending'
  })

  /**
   * Fetch specific dropdown data type from API and cache in localStorage
   */
  async function fetchSpecificDropdownData(dataType: string): Promise<void> {
    downloadProgress.value[dataType] = 'loading'

    let response = null

    switch (dataType) {
      case 'products':
        response = await useApi(
          'products?include=custom_fields_0&per_page=1000&sort=bezeichnung',
          'GET'
        )
        if (response?.data) {
          products.value = response.data
          console.log(`[DropdownData] ✅ Cached ${response.data.length} products`)
        }
        break

      case 'projectTypes':
        response = await useApi('project-types?per_page=1000&sort=sort_order', 'GET')
        if (response?.data) {
          projectTypes.value = response.data
          console.log(`[DropdownData] ✅ Cached ${response.data.length} project types`)
        }
        break

      case 'users':
        response = await useApi('users?filter[inactive]=false&per_page=1000&sort=name', 'GET')
        if (response?.data) {
          users.value = response.data
          console.log(`[DropdownData] ✅ Cached ${response.data.length} users`)
        }
        break

      case 'departments':
        response = await useApi('departments?per_page=1000&sort=name', 'GET')
        if (response?.data) {
          departments.value = response.data
          console.log(`[DropdownData] ✅ Cached ${response.data.length} departments`)
        }
        break

      case 'projectPhases':
        response = await useApi('project-phases?per_page=1000&sort=name', 'GET')
        if (response?.data) {
          projectPhases.value = response.data
          console.log(`[DropdownData] ✅ Cached ${response.data.length} project phases`)
        }
        break

      case 'calendarEventStatuses':
        response = await useApi('calendar-event-status?per_page=1000&sort=name', 'GET')
        if (response?.data) {
          calendarEventStatuses.value = response.data
          console.log(`[DropdownData] ✅ Cached ${response.data.length} calendar event statuses`)
        }
        break

      case 'calendarCategories':
        response = await useApi(
          'calendar-categories?per_page=1000&filter[active]=1&sort=name',
          'GET'
        )
        if (response?.data) {
          calendarCategories.value = response.data
          console.log(`[DropdownData] ✅ Cached ${response.data.length} calendar categories`)
        }
        break

      case 'damageReport':
        response = await useApi('custom-fields/projects', 'GET')
        if (response?.data?.custom_fields_0) {
          const s7Field = response.data.custom_fields_0.find((f: any) => f.column === 's_7')
          if (s7Field?.options) {
            damageReport.value = s7Field.options
            console.log(`[DropdownData] ✅ Cached ${s7Field.options.length} damage report options`)
          }
        }
        break
    }

    downloadProgress.value[dataType] = 'done'
    lastFetchTime.value = Date.now()
  }

  /**
   * Fetch all dropdown data from API and cache in localStorage
   * Called after login to ensure offline availability
   */
  async function fetchDropdownData(): Promise<void> {
    if (isFetching.value) {
      console.log('[DropdownData] Already fetching, skipping...')
      return
    }

    isFetching.value = true
    console.log('[DropdownData] 🔄 Fetching dropdown data for offline use...')

    // Reset all progress to loading
    Object.keys(downloadProgress.value).forEach((key) => {
      downloadProgress.value[key] = 'loading'
    })

    try {
      // Fetch all dropdown data in parallel with individual error handling
      const [
        productsRes,
        projectTypesRes,
        usersRes,
        departmentsRes,
        projectPhasesRes,
        calendarEventStatusesRes,
        calendarCategoriesRes,
        damageReportRes
      ] = await Promise.all([
        // Products with custom fields
        useApi('products?include=custom_fields_0&per_page=1000&sort=bezeichnung', 'GET')
          .then((res) => {
            downloadProgress.value.products = 'done'
            return res
          })
          .catch(() => {
            downloadProgress.value.products = 'error'
            return null
          }),
        // Project types
        useApi('project-types?per_page=1000&sort=sort_order', 'GET')
          .then((res) => {
            downloadProgress.value.projectTypes = 'done'
            return res
          })
          .catch(() => {
            downloadProgress.value.projectTypes = 'error'
            return null
          }),
        // Users
        useApi('users?filter[inactive]=false&per_page=1000&sort=name', 'GET')
          .then((res) => {
            downloadProgress.value.users = 'done'
            return res
          })
          .catch(() => {
            downloadProgress.value.users = 'error'
            return null
          }),
        // Departments
        useApi('departments?per_page=1000&sort=name', 'GET')
          .then((res) => {
            downloadProgress.value.departments = 'done'
            return res
          })
          .catch(() => {
            downloadProgress.value.departments = 'error'
            return null
          }),
        // Project phases
        useApi('project-phases?per_page=1000&sort=name', 'GET')
          .then((res) => {
            downloadProgress.value.projectPhases = 'done'
            return res
          })
          .catch(() => {
            downloadProgress.value.projectPhases = 'error'
            return null
          }),
        // Calendar event statuses
        useApi('calendar-event-status?per_page=1000&sort=name', 'GET')
          .then((res) => {
            downloadProgress.value.calendarEventStatuses = 'done'
            return res
          })
          .catch(() => {
            downloadProgress.value.calendarEventStatuses = 'error'
            return null
          }),
        // Calendar categories (all active)
        useApi('calendar-categories?per_page=1000&filter[active]=1&sort=name', 'GET')
          .then((res) => {
            downloadProgress.value.calendarCategories = 'done'
            return res
          })
          .catch(() => {
            downloadProgress.value.calendarCategories = 'error'
            return null
          }),
        // Damage report options (custom fields s_7)
        useApi('custom-fields/projects', 'GET')
          .then((res) => {
            downloadProgress.value.damageReport = 'done'
            return res
          })
          .catch(() => {
            downloadProgress.value.damageReport = 'error'
            return null
          })
      ])

      // Store fetched data
      if (productsRes?.data) {
        products.value = productsRes.data
        console.log(`[DropdownData] ✅ Cached ${productsRes.data.length} products`)
      }

      if (projectTypesRes?.data) {
        projectTypes.value = projectTypesRes.data
        console.log(`[DropdownData] ✅ Cached ${projectTypesRes.data.length} project types`)
      }

      if (usersRes?.data) {
        users.value = usersRes.data
        console.log(`[DropdownData] ✅ Cached ${usersRes.data.length} users`)
      }

      if (departmentsRes?.data) {
        departments.value = departmentsRes.data
        console.log(`[DropdownData] ✅ Cached ${departmentsRes.data.length} departments`)
      }

      if (projectPhasesRes?.data) {
        projectPhases.value = projectPhasesRes.data
        console.log(`[DropdownData] ✅ Cached ${projectPhasesRes.data.length} project phases`)
      }

      if (calendarEventStatusesRes?.data) {
        calendarEventStatuses.value = calendarEventStatusesRes.data
        console.log(
          `[DropdownData] ✅ Cached ${calendarEventStatusesRes.data.length} calendar event statuses`
        )
      }

      if (calendarCategoriesRes?.data) {
        calendarCategories.value = calendarCategoriesRes.data
        console.log(
          `[DropdownData] ✅ Cached ${calendarCategoriesRes.data.length} calendar categories`
        )
      }

      if (damageReportRes?.data?.custom_fields_0) {
        const s7Field = damageReportRes.data.custom_fields_0.find((f: any) => f.column === 's_7')
        if (s7Field?.options) {
          damageReport.value = s7Field.options
          console.log(`[DropdownData] ✅ Cached ${s7Field.options.length} damage report options`)
        }
      }

      // Update last fetch time
      lastFetchTime.value = Date.now()

      console.log('[DropdownData] ✅ All dropdown data cached successfully')
    } catch (error) {
      console.error('[DropdownData] ❌ Failed to fetch dropdown data:', error)
    } finally {
      isFetching.value = false
    }
  }

  /**
   * Get cached products
   */
  function getProducts(): any[] {
    return products.value
  }

  /**
   * Get cached project types
   */
  function getProjectTypes(): any[] {
    return projectTypes.value
  }

  /**
   * Get cached users
   */
  function getUsers(): any[] {
    return users.value
  }

  /**
   * Get cached departments
   */
  function getDepartments(): any[] {
    return departments.value
  }

  /**
   * Get cached project phases
   */
  function getProjectPhases(): any[] {
    return projectPhases.value
  }

  /**
   * Get cached calendar event statuses
   */
  function getCalendarEventStatuses(): any[] {
    return calendarEventStatuses.value
  }

  /**
   * Get cached calendar categories
   */
  function getCalendarCategories(): any[] {
    return calendarCategories.value
  }

  /**
   * Get cached damage report options
   */
  function getDamageReport(): any[] {
    return damageReport.value
  }

  /**
   * Check if dropdown data is available
   */
  function hasData(): boolean {
    return (
      products.value.length > 0 ||
      projectTypes.value.length > 0 ||
      users.value.length > 0 ||
      departments.value.length > 0 ||
      projectPhases.value.length > 0 ||
      calendarEventStatuses.value.length > 0 ||
      damageReport.value.length > 0
    )
  }

  /**
   * Check if data is stale (older than 24 hours)
   */
  function isDataStale(): boolean {
    const twentyFourHours = 24 * 60 * 60 * 1000
    return Date.now() - lastFetchTime.value > twentyFourHours
  }

  /**
   * Clear all cached dropdown data
   * Called on logout
   */
  function clearDropdownData(): void {
    products.value = []
    projectTypes.value = []
    users.value = []
    departments.value = []
    projectPhases.value = []
    calendarEventStatuses.value = []
    calendarCategories.value = []
    damageReport.value = []
    lastFetchTime.value = 0

    // Reset download progress
    Object.keys(downloadProgress.value).forEach((key) => {
      downloadProgress.value[key] = 'pending'
    })

    console.log('[DropdownData] 🗑️  Cleared all cached dropdown data')
  }

  return {
    // State
    products: readonly(products),
    projectTypes: readonly(projectTypes),
    users: readonly(users),
    departments: readonly(departments),
    projectPhases: readonly(projectPhases),
    calendarEventStatuses: readonly(calendarEventStatuses),
    calendarCategories: readonly(calendarCategories),
    damageReport: readonly(damageReport),
    isFetching: readonly(isFetching),
    lastFetchTime: readonly(lastFetchTime),
    downloadProgress: readonly(downloadProgress),

    // Actions
    fetchDropdownData,
    fetchSpecificDropdownData,
    getProducts,
    getProjectTypes,
    getUsers,
    getDepartments,
    getProjectPhases,
    getCalendarEventStatuses,
    getCalendarCategories,
    getDamageReport,
    hasData,
    isDataStale,
    clearDropdownData
  }
})

// Use HMR for this store
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDropdownDataStore, import.meta.hot))
}
